import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format } from 'date-fns';
import Grid from '@mui/material/Grid2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// `only` (a graph title) renders that one graph full width, for the dashboard's per-vital dialog.
const VitalGraphView = ({ data, dateRange, only = null }) => {
  const filteredData = data.filter(record => {
    if (!dateRange[0] || !dateRange[1]) return true;
    const recordDate = new Date(record.scanDatetime);

    const startDate = new Date(dateRange[0]);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(dateRange[1]);
    endDate.setHours(23, 59, 59, 999);
    
    return recordDate >= startDate && recordDate <= endDate;
  });

  // Sort the filtered data by date in ascending order
  const sortedData = [...filteredData].sort((a, b) => {
    const dateA = new Date(a.scanDatetime);
    const dateB = new Date(b.scanDatetime);
    return dateA - dateB;
  });

  const dates = sortedData.map(record => 
    format(new Date(record.scanDatetime), 'MM/dd')
  );

  const createDataset = (values, normalRange, abnormalRange, criticalRange) => {
    // Find the min and max values for the Y axis
    const dataMin = Math.min(...values, normalRange[0], abnormalRange ? abnormalRange[0] : Number.MAX_VALUE, criticalRange ? criticalRange[0] : Number.MAX_VALUE);
    const dataMax = Math.max(...values, normalRange[1], abnormalRange ? abnormalRange[1] : 0, criticalRange ? criticalRange[3] : 0);
    
    // Add padding to ensure all ranges are visible
    const yMin = Math.max(0, dataMin); // Ensure we don't go below 0 for vitals
    const yMax = dataMax

    // console.log('yMin:', yMin, 'yMax:', yMax);
    
    // A null critical bound means that side has no critical zone.
    const lowerCritical = criticalRange ? criticalRange[1] : null;
    const upperCritical = criticalRange ? criticalRange[2] : null;

    // Create background color datasets based on ranges
    const datasets = [
      // Data points - this stays on top
      {
        data: values,
        borderColor: '#29C0BB',
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: values.map(value => {
          if (lowerCritical !== null && value <= lowerCritical) return 'red';
          if (upperCritical !== null && value >= upperCritical) return 'red';
          if (value < normalRange[0] || value > normalRange[1]) return 'orange';
          return '#29C0BB';
        }),
        fill: false,
        showTooltip: true
      }
    ];
    
    // Add background datasets based on the available ranges
    
    // If we have a critical range defined, set up all regions
    if (criticalRange) {
      // First region: Bottom of chart to lower critical
      datasets.push({
        data: Array(dates.length).fill(yMin),
        borderColor: 'transparent',
        backgroundColor: 'rgba(255,200,200,0.5)', // Light red for critical range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Second region: Lower critical to lower abnormal
      datasets.push({
        data: Array(dates.length).fill(lowerCritical ?? yMin),
        borderColor: 'transparent',
        backgroundColor: '#FFEFDC', // Light orange for abnormal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Third region: Lower abnormal to lower normal
      datasets.push({
        data: Array(dates.length).fill(abnormalRange[0]),
        borderColor: 'transparent',
        backgroundColor: '#E8FFE9', // Light green for normal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Fourth region: Normal range (completely filled with green)
      datasets.push({
        data: Array(dates.length).fill(normalRange[0]),
        borderColor: 'transparent',
        backgroundColor: '#E8FFE9', // Light green for normal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Fifth region: Upper normal to upper abnormal
      datasets.push({
        data: Array(dates.length).fill(normalRange[1]),
        borderColor: 'transparent',
        backgroundColor: '#FFEFDC', // Light orange for abnormal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Sixth region: Upper abnormal to upper critical
      datasets.push({
        data: Array(dates.length).fill(abnormalRange[1]),
        borderColor: 'transparent',
        backgroundColor: 'rgba(255,200,200,0.5)', // Light red for critical range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Seventh region: Upper critical to top of chart
      datasets.push({
        data: Array(dates.length).fill(upperCritical ?? yMax),
        borderColor: 'transparent',
        backgroundColor: 'rgba(255,200,200,0.5)', // Light red for critical range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Top limit
      datasets.push({
        data: Array(dates.length).fill(yMax),
        borderColor: 'transparent',
        fill: true,
        pointRadius: 0,
        showTooltip: false
      });
    } 
    // If we only have abnormal range but no critical range
    else if (abnormalRange) {
      datasets.push({
        data: Array(dates.length).fill(0),
        borderColor: 'transparent',
        backgroundColor: '#FFEFDC', // Light orange for abnormal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      datasets.push({
        data: Array(dates.length).fill(yMin),
        borderColor: 'transparent',
        backgroundColor: '#FFEFDC', // Light orange for abnormal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      datasets.push({
        data: Array(dates.length).fill(abnormalRange[0]),
        borderColor: 'transparent',
        backgroundColor: '#E8FFE9', // Light green for normal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      datasets.push({
        data: Array(dates.length).fill(normalRange[0]),
        borderColor: 'transparent',
        backgroundColor: '#E8FFE9', // Light green for normal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      datasets.push({
        data: Array(dates.length).fill(normalRange[1]),
        borderColor: 'transparent',
        backgroundColor: '#FFEFDC', // Light orange for abnormal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      datasets.push({
        data: Array(dates.length).fill(abnormalRange[1]),
        borderColor: 'transparent',
        backgroundColor: '#FFEFDC', // Light orange for abnormal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      datasets.push({
        data: Array(dates.length).fill(yMax),
        borderColor: 'transparent',
        fill: true,
        pointRadius: 0,
        showTooltip: false
      });
    }
    // If we only have normal range
    else {
      // First region: Bottom of chart to lower normal
      datasets.push({
        data: Array(dates.length).fill(yMin),
        borderColor: 'transparent',
        backgroundColor: '#FFEFDC', // Light orange outside normal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Second region: Normal range
      datasets.push({
        data: Array(dates.length).fill(normalRange[0]),
        borderColor: 'transparent',
        backgroundColor: '#E8FFE9', // Light green for normal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Third region: Upper normal to top of chart
      datasets.push({
        data: Array(dates.length).fill(normalRange[1]),
        borderColor: 'transparent',
        backgroundColor: '#FFEFDC', // Light orange outside normal range
        fill: '+1',
        pointRadius: 0,
        showTooltip: false
      });
      
      // Top limit
      datasets.push({
        data: Array(dates.length).fill(yMax),
        borderColor: 'transparent',
        fill: true,
        pointRadius: 0,
        showTooltip: false
      });
    }
    
    return {
      labels: dates,
      datasets: datasets
    };
  };

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        filter: function(tooltipItem) {
          return tooltipItem.dataset.showTooltip;
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Dates',
          font: {
            size: 14,
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
  };

  const graphs = [
    {
      title: 'Heart Rate',
      data: createDataset(
        sortedData.map(record => record.heartRate),
        [60, 100],
        [60, 130],
        [40, 55, 130, 180] // Min possible value, lower critical, upper critical, max possible value
      ),
      yLabel: 'Heart Rate (bpm)',
      getRange: (data) => ({
        min: Math.min(...data.map(record => record.heartRate)),
        max: Math.max(...data.map(record => record.heartRate)),
      }),
    },
    {
      title: 'Blood Pressure',
      data: {
        labels: dates,
        datasets: [
          {
            label: 'SBP',
            data: sortedData.map(record => record.bloodPressure.sbp),
            borderColor: '#29C0BB',
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: sortedData.map(record => 
              record.bloodPressure.sbp >= 180 ? 'red' 
              : record.bloodPressure.sbp >= 120 || record.bloodPressure.sbp < 90 ? 'orange'
              : '#29C0BB'
            ),
            showTooltip: true
          },
          {
            label: 'DBP',
            data: sortedData.map(record => record.bloodPressure.dbp),
            borderColor: '#0383E0',
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: sortedData.map(record => 
              record.bloodPressure.dbp >= 120 ? 'red' 
              : record.bloodPressure.dbp >= 80 || record.bloodPressure.dbp < 60 ? 'orange'
              : '#0383E0'
            ),
            showTooltip: true
          },
        ],
      },
      yLabel: 'Blood Pressure (mmHg)',
      getRange: (data) => ({
        min: Math.min(
          ...data.map(record => record.bloodPressure.sbp),
          ...data.map(record => record.bloodPressure.dbp)
        ),
        max: Math.max(
          ...data.map(record => record.bloodPressure.sbp),
          ...data.map(record => record.bloodPressure.dbp)
        ),
      }),
    },
    {
      title: 'Heart Rate Variability',
      data: createDataset(
        sortedData.map(record => record.hrvSdnn),
        [20, 250], // vitals-chat: < 20 ms is abnormal, no upper limit
        [20, 250],
        null // No critical range
      ),
      yLabel: 'HRV (ms)',
      getRange: (data) => ({
        min: Math.min(...data.map(record => record.hrvSdnn)),
        max: Math.max(...data.map(record => record.hrvSdnn)),
      }),
    },
    {
      title: 'Oxygen Saturation',
      data: createDataset(
        sortedData.map(record => record.oxygenSaturation),
        [95, 100],
        [95, 100],
        [40, 88, null, 100] // Min possible value, lower critical, no upper critical (100% is normal), max possible value
      ),
      yLabel: 'Oxygen Saturation (%)',
      getRange: (data) => ({
        min: Math.min(...data.map(record => record.oxygenSaturation)),
        max: Math.max(...data.map(record => record.oxygenSaturation)),
      }),
    },
    {
      title: 'Stress Index',
      data: createDataset(
        sortedData.map(record => record.stressIndex),
        [0, 199], // vitals-chat: Low (< 50) and Normal (< 200) are both normal
        [0, 350], // 200-349 Moderate = abnormal
        [0, null, 350, 500] // >= 350 High / Very High = critical, no lower critical
      ),
      yLabel: 'Stress Index',
      getRange: (data) => ({
        min: Math.min(...data.map(record => record.stressIndex)),
        max: Math.max(...data.map(record => record.stressIndex)),
      }),
    },
    {
      title: 'Respiration Rate',
      data: createDataset(
        sortedData.map(record => record.respirationRate),
        [12, 20],
        [12, 35],
        [0, 0, 35, 60] // Min possible value, lower critical, upper critical, max possible value
      ),
      yLabel: 'Respiration Rate (bpm)',
      getRange: (data) => ({
        min: Math.min(...data.map(record => record.respirationRate)),
        max: Math.max(...data.map(record => record.respirationRate)),
      }),
    },
  ];

  return (
    <Grid container spacing={3}>
      {graphs.filter(graph => !only || graph.title === only).map((graph, index) => (
        <Grid size={{xs: 12, md: only ? 12 : 6}} key={index}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: 3, 
              borderRadius: 2,
              backgroundColor: 'white',
              height: '400px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              {graph.title}
            </Typography>
            <Box sx={{ flexGrow: 1, position: 'relative' }}>
              <Line
                data={graph.data}
                options={{
                  ...commonOptions,
                  scales: {
                    ...commonOptions.scales,
                    y: {
                      ...commonOptions.scales.y,
                      title: {
                        display: true,
                        text: graph.yLabel,
                        font: {
                          size: 14,
                        },
                      },
                    },
                  },
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Patient's {graph.title.toLowerCase()} is within the range of{' '}
              {graph.getRange(sortedData).min.toFixed(1)} - {graph.getRange(sortedData).max.toFixed(1)}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

export default VitalGraphView;